import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Zap, MessageCircle, BadgePercent, ArrowRight, BarChart3 } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import { api, track } from "../../lib/api";
import { captureAttribution } from "../../lib/attribution";
import { SearchBox, StickySearchCTA } from "../../components/b2c/SearchBox";
import { AirlineCard, RouteCard, SectionHeading } from "../../components/b2c/Blocks";

const THEMES = {
  classic: { hero: "bg-gradient-to-br from-[#00172D] via-[#0A2340] to-[#970C10]", overline: "text-[#E8767A]", badge: "bg-white/10" },
  "dark-luxe": { hero: "bg-[#0B0B10]", overline: "text-amber-400", badge: "bg-amber-400/10 border border-amber-400/25" },
  "gulf-gold": { hero: "bg-gradient-to-br from-[#2C1E05] via-[#6B4E12] to-[#B8860B]", overline: "text-amber-200", badge: "bg-white/10" },
  "fresh-teal": { hero: "bg-gradient-to-br from-[#042F2E] via-[#0F766E] to-[#0D9488]", overline: "text-teal-200", badge: "bg-white/10" },
};

export default function AffiliateLanding() {
  const { code } = useParams();
  const [affiliate, setAffiliate] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [airlines, setAirlines] = useState([]);
  const [routes, setRoutes] = useState([]);

  // CRITICAL: on mount, force this affiliate code into sessionStorage attribution
  // so every downstream search/handoff carries it, even if user didn't hit the URL with ?aff=code
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get("aff")) {
      params.set("aff", code);
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    }
    captureAttribution();
  }, [code]);

  useEffect(() => {
    api.get(`/affiliates/${code}`).then((r) => setAffiliate(r.data)).catch(() => setNotFound(true));
    api.get("/airlines").then((r) => setAirlines(r.data.slice(0, 6))).catch(() => {});
    api.get("/routes").then((r) => setRoutes(r.data.slice(0, 6))).catch(() => {});
  }, [code]);

  useEffect(() => {
    if (affiliate) track("affiliate_landing_view", `/a/${code}`, { code, affiliate_name: affiliate.name });
  }, [affiliate, code]);

  usePageMeta(
    affiliate ? `${affiliate.name}'s discounted flights — Farevault by Sellvago` : "Discounted flights — Farevault by Sellvago",
    "Book cheap airline tickets from Pakistan through this creator's affiliate storefront. Same fares, same instant e-tickets, same WhatsApp support."
  );

  const pauseNotice = affiliate?.status === "paused";

  const trustBadges = useMemo(() => [
    { i: ShieldCheck, t: "Amadeus-issued e-tickets" },
    { i: Zap, t: "Instant confirmation" },
    { i: MessageCircle, t: "WhatsApp support in Urdu & English" },
    { i: BadgePercent, t: "Multi-supplier discount fares" },
  ], []);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="affiliate-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Affiliate not found</h1>
        <p className="mt-3 text-slate-500 text-sm">{"This affiliate link isn't active. You can still book great fares at Farevault."}</p>
        <Link to="/flights" className="mt-6 inline-flex items-center gap-2 text-[#970C10] font-bold text-sm">
          Go to Farevault <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  if (!affiliate) {
    return <div className="min-h-[400px] flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  }

  const theme = THEMES[affiliate.theme] || THEMES.classic;

  return (
    <div data-testid="affiliate-landing" data-theme={affiliate.theme || "classic"}>
      <section className={`relative ${theme.hero} text-white pt-16 pb-24 px-4 sm:px-6`}>
        <div className="max-w-6xl mx-auto">
          <p className={`text-xs uppercase tracking-[0.2em] ${theme.overline} font-bold mb-3`}>
            Featured by {affiliate.name}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter leading-[1.05] max-w-3xl">
            {affiliate.custom_hero_headline || `Cheap flights from Pakistan — hand-picked for ${affiliate.name}'s community`}
          </h1>
          <p className="mt-5 text-white/85 text-base md:text-lg max-w-xl leading-relaxed">
            {affiliate.custom_hero_subtext || "Search live discounted fares on Emirates, Qatar, PIA, Saudia, Turkish Airlines and more. Instant e-tickets via Amadeus, WhatsApp help for every booking."}
          </p>

          {pauseNotice && (
            <div className="mt-5 max-w-xl bg-amber-500/20 border border-amber-500/40 rounded-lg p-3 text-xs text-amber-100">
              This affiliate link is temporarily paused, but you can still book normally on Farevault.
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {trustBadges.map((b) => (
              <div key={b.t} className={`flex items-center gap-2 ${theme.badge} backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold`}>
                <b.i className="w-3.5 h-3.5" />{b.t}
              </div>
            ))}
          </div>

          <div className="mt-10">
            <SearchBox page={`/a/${code}`} />
          </div>
        </div>
      </section>

      {airlines.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-20" data-testid="affiliate-airlines-section">
          <SectionHeading overline="Airlines" title={`${affiliate.name}'s favourite carriers`} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {airlines.map((a) => <AirlineCard key={a.slug} airline={a} />)}
          </div>
        </section>
      )}

      {routes.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-20" data-testid="affiliate-routes-section">
          <SectionHeading overline="Popular routes" title="Top-selling routes this month" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {routes.map((r) => <RouteCard key={r.slug} route={r} />)}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <div className="glass-dark rounded-2xl p-7 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-heading text-lg font-bold text-white">Are you {affiliate.name}?</p>
            <p className="text-sm text-slate-300 mt-1">View this link's live click, search and handoff stats.</p>
          </div>
          <Link
            to={`/a/${code}/stats`}
            className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-slate-100 transition-colors duration-200"
            data-testid="affiliate-landing-stats-link"
          >
            <BarChart3 className="w-4 h-4" /> View my stats
          </Link>
        </div>
      </section>

      <StickySearchCTA />
    </div>
  );
}
